import { Box, Tooltip, tooltipClasses, TooltipProps, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  IconCalendar,
  IconCertificate,
  IconClock,
  IconCloseCircle,
  IconCoin,
  IconCross,
  IconFlexPools,
  IconLocation,
  IconPause,
  IconProfileUsers,
  IconQR,
} from 'assets/img';
import { DateTime, Duration } from 'luxon';
import { FlexPoolOnlyIndicator } from 'shared/components/common/flexpool-only-indicator';
import { formatCurrencyCents, formatDateFromString } from 'shared/utils/formatting';
import { ReactNode } from 'react';
import { isFreelanceShift, isTempworkShift } from 'shared/services/shift-service-types';
import { hasClaimedState, isShowState } from 'shared/utils/shift-claim-state';
import { HourlyRates } from 'shared/components/planner/common/hourly-rates';
import { ShiftDetail } from 'api/services/shift/schema/shift-detail.schema';
import { ContextMenuButton, LwIconButton, ShiftDescriptionTooltip } from 'redesign';
import { useDownloadQRCodePDF } from 'shared/components/qr-code/hooks/use-download-qr-code-pdf/use-download-qr-code-pdf';
import { LevelQrCodeCanvas } from 'shared/components/qr-code/level-qr-code-canvas/level-qr-code-canvas';
import { pickShiftPDFOptions } from 'shared/components/qr-code/utils/pick-shift-pdf-options';
import { useQrCodeDownloadGuideDialog } from 'shared/components/qr-code/qr-code-download-dialog/use-qr-code-download-guide-dialog';
import { daysHoursMinutesUntil } from './utils/days-hours-minutes-until';
import { ShiftInfoTranslator } from './shift-info-button/shift-info-translator';
import { getQrCheckoutDeeplinkUrl } from './utils/get-qr-checkout-deeplink-url';
import { CertificatesDetail } from '../../../planner/common/certificates-detail';

export const ShiftDetailHead = ({
  data,
  close,
  actions,
  isArchiving,
  showTempWorkRate,
}: {
  data: ShiftDetail;
  close: () => void;
  actions: ReactNode;
  isArchiving: boolean;
  showTempWorkRate: boolean;
}) => {
  const shiftProperties = getShiftProperties(data, showTempWorkRate);

  const { canvasRef, handleDownload } = useDownloadQRCodePDF({
    ...pickShiftPDFOptions(data),
  });

  const { Element: DownloadGuideDialog, open: openDownloadGuideDialog } =
    useQrCodeDownloadGuideDialog({
      onDownload: handleDownload,
    });

  return (
    <Box>
      <LevelQrCodeCanvas canvasRef={canvasRef} targetUrl={getQrCheckoutDeeplinkUrl(data.id)} />
      <Box display="flex" flexDirection="row">
        <CompanyInfoBox>
          <Typography
            noWrap
            color="secondary"
            variant="body1"
            sx={{ fontWeight: 'bold', textOverflow: 'ellipsis' }}
          >
            {shiftProperties.businessUnitInfo}
          </Typography>
          <FlexPoolOnlyIndicator flexPoolOnly={data.flexPoolOnly}>
            <FullWidthTooltip title={shiftProperties.jobName} placement="bottom-start">
              <StyledJobName noWrap variant="h5">
                {shiftProperties.jobName}
              </StyledJobName>
            </FullWidthTooltip>
          </FlexPoolOnlyIndicator>
        </CompanyInfoBox>
        <Box display="flex" flexDirection="row" ml="auto" alignItems="middle" style={{ gap: 8 }}>
          <LwIconButton
            iconColorMode="stroke"
            color="secondary"
            onClick={openDownloadGuideDialog}
            data-testid="download-qr-code-button"
          >
            <IconQR width={24} height={24} />
          </LwIconButton>
          <ContextMenuButton loading={isArchiving} size="medium" color="secondary">
            {actions}
          </ContextMenuButton>
          <LwIconButton iconColorMode="stroke" color="secondary" onClick={close}>
            <IconCross width={24} height={24} />
          </LwIconButton>
        </Box>
      </Box>

      <PropertiesBox>
        <InfoLabelBox>
          <CenterTextBox>
            <StyledIcon as={IconProfileUsers} />
            <Typography noWrap variant="subtitle2">
              {shiftProperties.seats}
            </Typography>
          </CenterTextBox>
          <CenterTextBox>
            <StyledIcon as={IconCalendar} />
            <Typography noWrap variant="subtitle2">
              {shiftProperties.startDate}
            </Typography>
          </CenterTextBox>
          <CenterTextBox>
            <StyledIcon as={IconPause} />
            <Typography noWrap variant="subtitle2">
              {shiftProperties.cooldownPeriod}
            </Typography>
          </CenterTextBox>
        </InfoLabelBox>
        <InfoLabelBox>
          <CenterTextBox>
            <StyledIcon as={IconClock} />
            <Typography noWrap variant="subtitle2">
              {shiftProperties.duration}
            </Typography>
          </CenterTextBox>
          <CenterTextBox>
            <StyledIcon as={IconPause} />
            <Typography noWrap variant="subtitle2">
              {shiftProperties.break}
            </Typography>
          </CenterTextBox>
          <CenterTextBox>
            <StyledIcon as={IconFlexPools} />
            {shiftProperties.flexPoolNames}
          </CenterTextBox>
        </InfoLabelBox>
        <InfoLabelBox>
          <Box display="flex" flexDirection="row" marginBottom={3}>
            <HourlyRates
              freelanceWage={data.freelanceProperties?.hourlyRateCents ?? undefined}
              tempworkWage={data.tempWorkProperties?.grossHourlyRateCents ?? undefined}
              showTempWorkRate={showTempWorkRate}
            />
          </Box>
          <CenterTextBox>
            <StyledIcon as={IconCoin} />
            <Typography noWrap variant="subtitle2">
              {shiftProperties.totalRate}
            </Typography>
          </CenterTextBox>
          <CenterTextBox>
            <StyledIcon as={IconCertificate} />
            <CertificatesDetail certificates={shiftProperties.certificates} />
          </CenterTextBox>
        </InfoLabelBox>
        <InfoLabelBox>
          <CenterTextBox>
            <StyledIcon as={IconCloseCircle} />
            <Typography noWrap variant="subtitle2">
              {shiftProperties.cancelationPeriod}
            </Typography>
          </CenterTextBox>
          <CenterTextBox>
            <StyledIcon as={IconLocation} />
            <Tooltip title={data.location.address}>
              <Typography noWrap variant="subtitle2">
                {shiftProperties.location}
              </Typography>
            </Tooltip>
          </CenterTextBox>
          <ShiftDescriptionTooltip
            descriptionShort={shiftProperties.descriptionShort}
            descriptionLong={shiftProperties.descriptionLong}
            descriptionLawRequirements={shiftProperties.descriptionLawRequirements}
          />
        </InfoLabelBox>
      </PropertiesBox>
      {DownloadGuideDialog}
    </Box>
  );
};

const displayTotalRate = (
  data: ShiftDetail,
  shiftTimeLengthInMs: number,
  shiftBreakLengthInMs: number,
  showTempWorkRate: boolean
) => {
  let totalRate = '- ';

  if (isFreelanceShift(data)) {
    totalRate = `${formatCurrencyCents(
      ((shiftTimeLengthInMs - shiftBreakLengthInMs) / 1000 / 60 / 60) *
        data.freelanceProperties.hourlyRateCents
    )} totaal`;
  } else if (isTempworkShift(data) && showTempWorkRate) {
    totalRate = `${formatCurrencyCents(
      ((shiftTimeLengthInMs - shiftBreakLengthInMs) / 1000 / 60 / 60) *
        data.tempWorkProperties.grossHourlyRateCents
    )} totaal`;
  }

  return totalRate;
};

const displayFlexPools = (flexPoolNames: { id: string; name: string }[]) => (
  <Tooltip
    title={flexPoolNames.length > 0 ? flexPoolNames.map((pool) => pool.name).join(', ') : null}
  >
    <Typography noWrap variant="subtitle2">
      {`${flexPoolNames.length} flexpool(s)`}
    </Typography>
  </Tooltip>
);

const getShiftProperties = (data: ShiftDetail, showTempWorkRate: boolean) => {
  const acceptedClaimantsCount = data.claimants.filter(hasClaimedState).filter(isShowState).length;
  const shiftTimeLengthInMs = DateTime.fromISO(data.isoEndDateTime).diff(
    DateTime.fromISO(data.isoStartDateTime)
  ).milliseconds;
  const shiftBreakLengthInMs = data.breakMinutes * 60 * 1000;
  const duration = Duration.fromMillis(shiftTimeLengthInMs).normalize().toFormat('hh:mm');

  const translator = new ShiftInfoTranslator();

  const cooldownDuration = Duration.fromObject({ minutes: data.workCooldownMinutes })
    .normalize()
    .toFormat('hh:mm');

  const certificates = [
    ...new Set(
      [...data.jobTypeCertificates, ...data.shiftCertificates].map(
        (certificate) => certificate.name
      )
    ),
  ];

  return {
    businessUnitInfo: `${data.companyName} / ${data.branchOfficeName}, ${data.departmentName}`,
    jobName: data.name,
    history: translator.translateEvents(data.history),
    seats: `${acceptedClaimantsCount} / ${data.maxClaimants} flexwerkers`,
    startDate: formatDateFromString(data.startDate),
    endDate: formatDateFromString(data.endDate),
    startTime: data.startTime,
    endTime: data.endTime,
    duration: `${data.startTime} - ${data.endTime} (${duration} uren)`,
    break: `${data.breakMinutes === 0 ? 'Geen' : `${data.breakMinutes} min. pauze`}`,
    totalRate: displayTotalRate(data, shiftTimeLengthInMs, shiftBreakLengthInMs, showTempWorkRate),
    cancelationPeriod: `${daysHoursMinutesUntil(data.mutableUntilDate, 'Verstreken')}`,
    cooldownPeriod: `${cooldownDuration} rustperiode na shift`,
    flexPoolNames: displayFlexPools(data.flexPools),
    location: `${data.location.street} ${data.location.houseNumber}`,
    certificates,
    descriptionShort: data.descriptionShort,
    descriptionLong: data.descriptionLong,
    descriptionLawRequirements: data.descriptionLawRequirements,
  };
};

const StyledIcon = styled('svg')(({ theme }) => ({
  opacity: 0.6,
  marginRight: theme.spacing(3),
  flexShrink: 0,
  width: 24,
  height: 24,
}));

const StyledJobName = styled(Typography)(({ theme }) => ({
  flex: 1,
  marginTop: theme.spacing(2),
}));

const CenterTextBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginBottom: theme.spacing(3),
}));

const CompanyInfoBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const InfoLabelBox = styled(CompanyInfoBox)(() => ({
  flex: 1,
  minWidth: 0,
}));

const PropertiesBox = styled(Box)(({ theme }) => ({
  gap: theme.spacing(3),
  display: 'flex',
  flexDirection: 'row',
  marginTop: theme.spacing(5),
  '& svg': {
    opacity: 0.6,
  },
}));

const FullWidthTooltip = styled((props: TooltipProps) => {
  const { className, ...rest } = props;
  return <Tooltip {...rest} classes={{ popper: className }} />;
})(() => ({
  flex: 1,
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
  },
})) as React.ComponentType<TooltipProps>;
